import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/lib/contexts/AuthContext'
import { useSiteSafe } from '@/lib/contexts/SiteContext'
import { useWorkspaceSafe } from '@/lib/contexts/WorkspaceContext'
import { useSubscription } from '@/lib/contexts/SubscriptionContext'
import { getSubscriptionUsage } from '@/lib/api/api'
import { getFeatureAccessForPlan } from '@/lib/constants/pricing'
import type { PlanTier, PlanUsageCounter, PlanUsageResponse, RevenueAttributionUsage } from '@/lib/types/billing'

// Helper to get propertyId from context or options — mirrors the pattern in
// useAnalytics.ts / useExperiments.ts (each hook file keeps its own copy
// rather than sharing a context-coupled utility).
function usePropertyId(optionsPropertyId?: string) {
  const siteContext = useSiteSafe()
  const workspaceContext = useWorkspaceSafe()

  return (
    optionsPropertyId ||
    (workspaceContext ? workspaceContext.selectedPropertyId : null) ||
    siteContext?.currentSite?.propertyId ||
    siteContext?.id
  )
}

// Trial bypass still surfaces the real usage count when one is known (live
// data or a caller-supplied local count), so labels like "3 pages" don't
// read 0 for trial users.
const unlimitedCounter = (scope: PlanUsageCounter['scope'], current = 0): PlanUsageCounter => ({
  current,
  limit: Infinity,
  canAdd: true,
  isUnlimited: true,
  scope,
})

/**
 * Canceled subscription: existing work stays readable, creating anything is
 * paywalled. The real `current` and `limit` are preserved so meters and copy
 * still describe what the account has — only `canAdd` closes.
 */
const readOnlyCounter = (
  counter: PlanUsageCounter | undefined,
  scope: PlanUsageCounter['scope'],
  fallbackLimit: number,
  fallbackCurrent?: number,
): PlanUsageCounter => ({
  current: counter?.current ?? fallbackCurrent ?? 0,
  limit: counter?.limit ?? fallbackLimit,
  canAdd: false,
  isUnlimited: false,
  scope,
})

/**
 * Client-side estimate of a counter when the usage endpoint hasn't answered
 * yet (loading) or is unavailable (error). Mirrors the plan → limit mapping
 * the heatmap gate used before this endpoint existed (`pricing.ts` feature
 * flags), so meters show a real plan limit instead of always claiming
 * unlimited during a hiccup.
 *
 * When the caller supplied a locally-known `current` count (via
 * `fallbackCounts`), `canAdd` is computed against it — restoring the old
 * fully client-side gate (e.g. heatmaps' `pages.length >= heatmapLimit`).
 * Without a local count there's nothing to compare, so `canAdd` stays `true`
 * (fail open) — only real data is trusted to block creation.
 */
const fallbackCounter = (
  limit: number,
  scope: PlanUsageCounter['scope'],
  current?: number,
): PlanUsageCounter => {
  const isUnlimited = !Number.isFinite(limit)
  return {
    current: current ?? 0,
    limit,
    canAdd: isUnlimited || current === undefined || current < limit,
    isUnlimited,
    scope,
  }
}

/** Locally-known usage counts a surface can feed the fallback path. */
interface PlanUsageFallbackCounts {
  funnels?: number
  heatmaps?: number
  splitTests?: number
  teamSeats?: number
  websites?: number
}

interface PlanUsage {
  /** Raw response from the endpoint, or null while loading/on error. */
  data: PlanUsageResponse | null
  isLoading: boolean
  error: Error | null

  funnels: PlanUsageCounter
  heatmaps: PlanUsageCounter
  splitTests: PlanUsageCounter
  teamSeats: PlanUsageCounter
  websites: PlanUsageCounter
  revenueAttribution: RevenueAttributionUsage | null
  metadata: PlanUsageResponse['metadata'] | null

  refetch: () => void
}

/**
 * usePlanUsage — app-wide plan usage/limits, backed by
 * `GET /api/v1/subscription/:propertyId/usage`.
 *
 * Every counter here encodes two policies so callers never have to think
 * about them:
 *
 *  - Trial bypass: an active trial reports every counter as unlimited /
 *    `canAdd: true`, matching the existing heatmap gate and FeatureGate.
 *  - Read-only after cancel: a churned paid account (`isReadOnly`) reports
 *    `canAdd: false` on every counter while keeping its real counts. That one
 *    switch is what turns every create CTA in the app into an Upgrade button
 *    after a cancel, while the customer's existing data stays readable.
 *  - Fallback / fail open: while the query is loading or errors, counters
 *    fall back to the client-side plan → limit mapping the heatmap gate used
 *    before this endpoint existed (`getFeatureAccessForPlan` in
 *    `pricing.ts`). If the surface passed its locally-known count via
 *    `fallbackCounts` (e.g. heatmaps passes `pages.length`), `canAdd` is
 *    gated against it so meters/banners/CTAs keep working exactly like the
 *    old implementation during an outage. Without a local count, `canAdd`
 *    stays `true` — a usage-endpoint outage must never block a creation flow
 *    off a guess.
 *
 * Creation/deletion mutations on each surface should invalidate the
 * `['plan-usage']` query key (via `useInvalidatePlanUsage()`) so counts stay
 * fresh immediately after an add/remove — across every property, since
 * account-scoped counters are cached per propertyId.
 */
export function usePlanUsage(options?: {
  propertyId?: string
  /**
   * Locally-known usage counts (e.g. heatmaps' `pages.length`), used only
   * by the fallback path when the endpoint can't answer. Websites default
   * to `SubscriptionContext`'s `websitesUsed`, which is always available.
   */
  fallbackCounts?: PlanUsageFallbackCounts
}): PlanUsage {
  const { token } = useAuth()
  const propertyId = usePropertyId(options?.propertyId)
  const { hasActiveTrial, currentPlan, websiteLimit, websitesUsed, isReadOnly } = useSubscription()
  const fallbackCounts = options?.fallbackCounts

  const query = useQuery({
    queryKey: ['plan-usage', propertyId],
    queryFn: () => {
      if (!token || !propertyId) throw new Error('Token and propertyId required')
      return getSubscriptionUsage(propertyId, token)
    },
    enabled: !!token && !!propertyId,
    staleTime: 30 * 1000,
  })

  const data = query.data ?? null
  const fetchUnavailable = query.isLoading || query.isError
  const features = getFeatureAccessForPlan(currentPlan as PlanTier)

  const resolveCounter = (
    counter: PlanUsageCounter | undefined,
    scope: PlanUsageCounter['scope'],
    fallbackLimit: number,
    fallbackCurrent?: number,
  ): PlanUsageCounter => {
    // Read-only wins over the trial bypass: a stale trial flag on a canceled
    // account must not reopen creation.
    if (isReadOnly) return readOnlyCounter(counter, scope, fallbackLimit, fallbackCurrent)
    if (hasActiveTrial) return unlimitedCounter(scope, counter?.current ?? fallbackCurrent)
    if (fetchUnavailable || !counter) return fallbackCounter(fallbackLimit, scope, fallbackCurrent)
    return counter
  }

  return {
    data,
    isLoading: query.isLoading,
    error: query.error as Error | null,

    funnels: resolveCounter(data?.funnels, 'property', features.funnelLimit, fallbackCounts?.funnels),
    heatmaps: resolveCounter(data?.heatmaps, 'property', features.heatmapLimit, fallbackCounts?.heatmaps),
    splitTests: resolveCounter(data?.splitTests, 'account', features.activeSplitTestLimit, fallbackCounts?.splitTests),
    teamSeats: resolveCounter(data?.teamSeats, 'account', features.teamSeats, fallbackCounts?.teamSeats),
    // `websiteLimit: 0` from SubscriptionContext means "not loaded yet", not
    // "zero sites allowed" — treat it as unknown so it can't false-block.
    websites: resolveCounter(
      data?.websites,
      'account',
      websiteLimit > 0 ? websiteLimit : Infinity,
      fallbackCounts?.websites ?? websitesUsed,
    ),
    revenueAttribution: hasActiveTrial ? null : data?.revenueAttribution ?? null,
    metadata: data?.metadata ?? null,

    refetch: () => {
      query.refetch()
    },
  }
}

/**
 * Returns a callback that invalidates every cached plan-usage query. Call
 * this from creation/deletion mutation `onSuccess` handlers so meters/banners
 * reflect the new count immediately. Invalidation is deliberately not scoped
 * to one property: account-scoped counters (split tests, seats, websites)
 * are cached under every propertyId that queried them, so freeing a slot on
 * one site must refresh the others too.
 */
export function useInvalidatePlanUsage() {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries({ queryKey: ['plan-usage'] })
  }
}
